<template>
  <div class="fixed inset-0 flex flex-col bg-background">
    <!-- Header -->
    <div class="flex items-center justify-between px-3 py-2 border-b shrink-0 bg-background z-10">
      <div class="flex items-center gap-2">
        <button @click="$emit('close')" class="p-1 -ml-1">
          <ChevronLeft class="w-5 h-5" />
        </button>
        <h2 class="text-sm font-semibold truncate">{{ displayName }}</h2>
      </div>
      <Button size="sm" @click="$emit('save')" :disabled="isSaving">
        {{ isSaving ? 'Saving...' : 'Save' }}
      </Button>
    </div>

    <!-- Canvas Area - takes remaining space above drawer -->
    <div 
      class="flex-1 relative overflow-hidden bg-muted/20"
      :style="{ paddingBottom: `${drawerHeight}px` }"
    >
      <slot name="canvas" />
    </div>

    <!-- Bottom Drawer -->
    <div 
      ref="drawerRef"
      class="fixed left-0 right-0 bottom-0 bg-background border-t rounded-t-xl shadow-2xl z-20 flex flex-col"
      :style="{ height: `${drawerHeight}px` }"
    >
      <!-- Drag Handle -->
      <div 
        class="flex justify-center py-2 cursor-grab active:cursor-grabbing touch-none"
        @pointerdown="startDrag"
      >
        <div class="w-10 h-1 bg-muted-foreground/30 rounded-full" />
      </div>

      <!-- Tab Bar -->
      <div class="grid grid-cols-3 border-b shrink-0 px-2">
        <button
          v-for="tab in tabs"
          :key="tab.id"
          @click="activeTab = tab.id"
          class="flex flex-col items-center gap-0.5 py-2 transition-colors"
          :class="activeTab === tab.id ? 'text-primary' : 'text-muted-foreground'"
        >
          <component :is="tab.icon" class="w-4 h-4" />
          <span class="text-[10px] font-medium">{{ tab.label }}</span>
        </button>
      </div>

      <!-- Panel Content -->
      <div class="flex-1 overflow-y-auto overscroll-contain">
        <!-- Layers Panel -->
        <div v-if="activeTab === 'layers'" class="p-3">
          <slot name="layers" />
        </div>

        <!-- Properties Panel -->
        <div v-if="activeTab === 'props'" class="p-3">
          <slot name="props" />
        </div>

        <!-- Library Panel -->
        <div v-if="activeTab === 'library'" class="p-3">
          <slot name="library" />
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted, markRaw } from 'vue'
import { ChevronLeft, Layers, Settings, Image } from 'lucide-vue-next'
import { Button } from '@/components/ui/button'

defineProps<{
  displayName: string
  isSaving?: boolean
}>()

defineEmits<{
  close: []
  save: []
}>()

// Drawer snap points (in pixels from bottom)
const SNAP_COLLAPSED = 120
const SNAP_HALF = Math.round(window.innerHeight * 0.45)
const SNAP_EXPANDED = Math.round(window.innerHeight * 0.85)

const drawerRef = ref<HTMLElement | null>(null)
const drawerHeight = ref(SNAP_HALF)
const activeTab = ref<'layers' | 'props' | 'library'>('layers')

// Tab definitions with icons
const tabs = computed(() => [
  { id: 'layers' as const, label: 'Layers', icon: markRaw(Layers) },
  { id: 'props' as const, label: 'Props', icon: markRaw(Settings) },
  { id: 'library' as const, label: 'Library', icon: markRaw(Image) },
])

// Drag handling
let startY = 0
let startHeight = 0
let isDragging = false

function startDrag(e: PointerEvent) {
  isDragging = true
  startY = e.clientY
  startHeight = drawerHeight.value
  
  document.addEventListener('pointermove', onDrag)
  document.addEventListener('pointerup', endDrag)
  document.addEventListener('pointercancel', endDrag)
}

function onDrag(e: PointerEvent) {
  if (!isDragging) return
  
  const deltaY = startY - e.clientY
  const newHeight = Math.max(SNAP_COLLAPSED, Math.min(SNAP_EXPANDED, startHeight + deltaY))
  drawerHeight.value = newHeight
}

function endDrag() {
  isDragging = false
  
  document.removeEventListener('pointermove', onDrag)
  document.removeEventListener('pointerup', endDrag)
  document.removeEventListener('pointercancel', endDrag)
  
  // Snap to nearest point
  const snapPoints = [SNAP_COLLAPSED, SNAP_HALF, SNAP_EXPANDED]
  let closest = SNAP_HALF
  let minDist = Infinity
  
  for (const point of snapPoints) {
    const dist = Math.abs(drawerHeight.value - point)
    if (dist < minDist) {
      minDist = dist
      closest = point
    }
  }
  
  // Animate to snap point
  drawerHeight.value = closest
}

// Switch to props tab when a layer is selected
function switchToProps() {
  activeTab.value = 'props'
}

// Expose for parent
defineExpose({
  switchToProps,
  activeTab
})
</script>

<style scoped>
/* Smooth transitions for drawer height */
.fixed[style*="height"] {
  transition: height 0.2s ease-out;
}
</style>
